import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Edit, Trash2, Archive, ChevronRight, Search } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { toast } from '@/components/ui/toaster';
import { useAuth, useHasRole } from '@/features/auth/AuthProvider';
import type { Category } from '@/types/schemas';
import {
  archiveCategory,
  deleteCategory,
  fetchBrands,
  fetchCategories,
} from '../setupQueries';
import { fetchAllInventory } from '../inventoryQueries';
import { CategoryFormDialog } from '../CategoryFormDialog';
import { CategoryDetailDialog } from '../CategoryDetailDialog';
import { cn } from '@/lib/utils';

type SortField = 'name' | 'codePrefix' | 'brands' | 'products' | 'threshold';

type ColumnKey = 'name' | 'prefix' | 'brands' | 'products' | 'lowStock' | 'actions';

const COLUMN_LABELS: Record<ColumnKey, string> = {
  name: 'Category',
  prefix: 'Prefix',
  brands: 'Brands',
  products: 'Products',
  lowStock: 'Low stock',
  actions: 'Actions',
};

const DEFAULT_COLUMN_WIDTHS: Record<ColumnKey, number> = {
  name: 200,
  prefix: 96,
  brands: 80,
  products: 88,
  lowStock: 96,
  actions: 168,
};

const COLUMN_ORDER: ColumnKey[] = ['name', 'prefix', 'brands', 'products', 'lowStock', 'actions'];

const MIN_COLUMN_WIDTH = 64;

const CELL_BORDER = 'border border-border';
const RESIZE_HANDLE =
  'absolute right-0 top-0 z-10 h-full w-1.5 cursor-col-resize touch-none select-none hover:bg-gold-400/70 active:bg-gold-500';

function useColumnResize(initial: Record<ColumnKey, number>) {
  const [widths, setWidths] = useState(initial);
  const dragRef = useRef<{ key: ColumnKey; startX: number; startWidth: number } | null>(null);

  const startResize = useCallback((key: ColumnKey, clientX: number) => {
    dragRef.current = { key, startX: clientX, startWidth: widths[key] };
  }, [widths]);

  useEffect(() => {
    function onMove(e: MouseEvent) {
      if (!dragRef.current) return;
      const { key, startX, startWidth } = dragRef.current;
      const next = Math.max(MIN_COLUMN_WIDTH, startWidth + (e.clientX - startX));
      setWidths((prev) => ({ ...prev, [key]: next }));
    }

    function onUp() {
      dragRef.current = null;
    }

    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onUp);
    return () => {
      window.removeEventListener('mousemove', onMove);
      window.removeEventListener('mouseup', onUp);
    };
  }, []);

  const resetWidths = useCallback(() => setWidths({ ...DEFAULT_COLUMN_WIDTHS }), []);

  return { widths, startResize, resetWidths };
}

function ResizableHeader({
  column,
  width,
  align,
  onResizeStart,
  resizable = true,
}: {
  column: ColumnKey;
  width: number;
  align: 'left' | 'center' | 'right';
  onResizeStart: (key: ColumnKey, clientX: number) => void;
  resizable?: boolean;
}) {
  return (
    <th
      style={{ width, minWidth: width, maxWidth: width }}
      className={cn(
        CELL_BORDER,
        'relative bg-muted/40 px-3 py-2 text-[11px] font-semibold uppercase text-muted-foreground',
        align === 'center' && 'text-center',
        align === 'right' && 'text-right',
        align === 'left' && 'text-left',
      )}
    >
      {COLUMN_LABELS[column]}
      {resizable && (
        <div
          role="separator"
          aria-orientation="vertical"
          aria-label={`Resize ${COLUMN_LABELS[column]} column`}
          className={RESIZE_HANDLE}
          onMouseDown={(e) => {
            e.preventDefault();
            e.stopPropagation();
            onResizeStart(column, e.clientX);
          }}
        />
      )}
    </th>
  );
}

export function CategoriesTab() {
  const qc = useQueryClient();
  const { user } = useAuth();
  const canWrite = useHasRole('owner');
  const canDelete = useHasRole('owner');
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState<Category | null>(null);
  const [detailCategory, setDetailCategory] = useState<Category | null>(null);
  const [detailOpen, setDetailOpen] = useState(false);
  const [search, setSearch] = useState('');
  const [sortField, setSortField] = useState<SortField>('name');
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('asc');
  const { widths, startResize, resetWidths } = useColumnResize(DEFAULT_COLUMN_WIDTHS);

  const tableWidth = useMemo(
    () => COLUMN_ORDER.reduce((sum, key) => sum + widths[key], 0),
    [widths],
  );

  const { data: categories = [], isLoading } = useQuery({
    queryKey: ['inventory-categories'],
    queryFn: fetchCategories,
  });

  const { data: allBrands = [] } = useQuery({
    queryKey: ['inventory-brands'],
    queryFn: () => fetchBrands(),
  });

  const { data: products = [] } = useQuery({
    queryKey: ['inventory'],
    queryFn: fetchAllInventory,
  });

  const brandCountByCategory = useMemo(() => {
    const counts = new Map<string, number>();
    for (const brand of allBrands) {
      if (brand.categoryId) {
        counts.set(brand.categoryId, (counts.get(brand.categoryId) ?? 0) + 1);
      }
    }
    return counts;
  }, [allBrands]);

  const productCountByCategory = useMemo(() => {
    const counts = new Map<string, number>();
    for (const product of products) {
      if (product.categoryId) {
        counts.set(product.categoryId, (counts.get(product.categoryId) ?? 0) + 1);
      }
    }
    return counts;
  }, [products]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    let list = categories;
    if (q) {
      list = list.filter(
        (c) =>
          c.name.toLowerCase().includes(q) ||
          c.codePrefix.toLowerCase().includes(q),
      );
    }
    list = [...list].sort((a, b) => {
      let cmp = 0;
      if (sortField === 'name') cmp = a.name.localeCompare(b.name);
      else if (sortField === 'codePrefix') cmp = a.codePrefix.localeCompare(b.codePrefix);
      else if (sortField === 'brands') {
        cmp = (brandCountByCategory.get(a.id) ?? 0) - (brandCountByCategory.get(b.id) ?? 0);
      } else if (sortField === 'products') {
        cmp = (productCountByCategory.get(a.id) ?? 0) - (productCountByCategory.get(b.id) ?? 0);
      } else cmp = a.lowStockThresholdDefault - b.lowStockThresholdDefault;
      return sortDir === 'asc' ? cmp : -cmp;
    });
    return list;
  }, [categories, search, sortField, sortDir, brandCountByCategory, productCountByCategory]);

  const archive = useMutation({
    mutationFn: (id: string) => archiveCategory(id, user?.uid ?? ''),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['inventory-categories'] });
      toast({ title: 'Category archived', variant: 'success' });
    },
  });

  const remove = useMutation({
    mutationFn: deleteCategory,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['inventory-categories'] });
      toast({ title: 'Category deleted', variant: 'success' });
    },
  });

  function openCategory(cat: Category) {
    setDetailCategory(cat);
    setDetailOpen(true);
  }

  function toggleSort(field: SortField) {
    if (sortField === field) setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
    else {
      setSortField(field);
      setSortDir('asc');
    }
  }

  if (isLoading) return <p className="text-sm text-muted-foreground p-8 text-center">Loading…</p>;

  return (
    <>
      <div className="mb-4 flex flex-col gap-3 sm:flex-row sm:items-center">
        <div className="relative flex-1">
          <Search className="absolute left-2 top-2.5 h-4 w-4 text-muted-foreground" />
          <Input
            className="pl-8"
            placeholder="Search by name or prefix…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>
        {canWrite && (
          <Button variant="gold" onClick={() => { setEditing(null); setOpen(true); }}>
            Create category
          </Button>
        )}
      </div>

      <div className="mb-2 flex flex-wrap items-center gap-2 text-xs">
        {(['name', 'codePrefix', 'brands', 'products', 'threshold'] as SortField[]).map((f) => (
          <button
            key={f}
            type="button"
            onClick={() => toggleSort(f)}
            className={cn(
              'rounded border px-2 py-1 capitalize',
              sortField === f ? 'bg-gold-100 border-gold-400' : 'text-muted-foreground',
            )}
          >
            {f === 'codePrefix' ? 'prefix' : f === 'threshold' ? 'low stock' : f}{' '}
            {sortField === f ? (sortDir === 'asc' ? '↑' : '↓') : ''}
          </button>
        ))}
        <button
          type="button"
          onClick={resetWidths}
          className="rounded border px-2 py-1 text-muted-foreground hover:border-gold-400 hover:text-foreground"
        >
          Reset column widths
        </button>
      </div>

      {filtered.length === 0 ? (
        <p className="p-8 text-center text-sm text-muted-foreground">No categories found.</p>
      ) : (
        <div className="overflow-x-auto rounded-lg border border-border">
          <table
            className="border-collapse text-sm"
            style={{ width: tableWidth, minWidth: '100%', tableLayout: 'fixed' }}
          >
            <colgroup>
              {COLUMN_ORDER.map((key) => (
                <col key={key} style={{ width: widths[key] }} />
              ))}
            </colgroup>
            <thead>
              <tr>
                <ResizableHeader column="name" width={widths.name} align="left" onResizeStart={startResize} />
                <ResizableHeader column="prefix" width={widths.prefix} align="left" onResizeStart={startResize} />
                <ResizableHeader column="brands" width={widths.brands} align="center" onResizeStart={startResize} />
                <ResizableHeader column="products" width={widths.products} align="center" onResizeStart={startResize} />
                <ResizableHeader column="lowStock" width={widths.lowStock} align="center" onResizeStart={startResize} />
                <ResizableHeader column="actions" width={widths.actions} align="right" onResizeStart={startResize} />
              </tr>
            </thead>
            <tbody>
              {filtered.map((cat) => {
                const brandCount = brandCountByCategory.get(cat.id) ?? 0;
                const productCount = productCountByCategory.get(cat.id) ?? 0;
                return (
                  <tr
                    key={cat.id}
                    className="cursor-pointer hover:bg-muted/20"
                    onClick={() => openCategory(cat)}
                  >
                    <td className={cn(CELL_BORDER, 'px-3 py-2.5 font-medium')}>{cat.name}</td>
                    <td className={cn(CELL_BORDER, 'px-3 py-2.5 font-mono text-xs')}>{cat.codePrefix}</td>
                    <td className={cn(CELL_BORDER, 'px-3 py-2.5 text-center tabular-nums')}>{brandCount}</td>
                    <td className={cn(CELL_BORDER, 'px-3 py-2.5 text-center tabular-nums')}>{productCount}</td>
                    <td className={cn(CELL_BORDER, 'px-3 py-2.5 text-center tabular-nums')}>
                      {cat.lowStockThresholdDefault}
                    </td>
                    <td className={cn(CELL_BORDER, 'px-3 py-2.5')}>
                      <div className="flex justify-end gap-0.5">
                        <span className="flex h-7 w-7 items-center justify-center text-muted-foreground">
                          <ChevronRight className="h-3.5 w-3.5" />
                        </span>
                        {canWrite && (
                          <Button
                            variant="ghost"
                            size="icon"
                            className="h-7 w-7"
                            onClick={(e) => {
                              e.stopPropagation();
                              setEditing(cat);
                              setOpen(true);
                            }}
                          >
                            <Edit className="h-3.5 w-3.5" />
                          </Button>
                        )}
                        {canWrite && (
                          <Button
                            variant="ghost"
                            size="icon"
                            className="h-7 w-7"
                            onClick={(e) => {
                              e.stopPropagation();
                              archive.mutate(cat.id);
                            }}
                          >
                            <Archive className="h-3.5 w-3.5" />
                          </Button>
                        )}
                        {canDelete && (
                          <Button
                            variant="ghost"
                            size="icon"
                            className="h-7 w-7 text-destructive"
                            onClick={(e) => {
                              e.stopPropagation();
                              if (confirm(`Delete "${cat.name}"?`)) remove.mutate(cat.id);
                            }}
                          >
                            <Trash2 className="h-3.5 w-3.5" />
                          </Button>
                        )}
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      <p className="mt-2 text-xs text-muted-foreground">
        {filtered.length} of {categories.length} categories · Click a row to manage brands and products · Drag column edges to resize
      </p>

      <CategoryFormDialog open={open} onOpenChange={setOpen} editing={editing} />
      <CategoryDetailDialog
        category={detailCategory}
        open={detailOpen}
        onOpenChange={setDetailOpen}
      />
    </>
  );
}
